import { registry, instancePerContainerCachingFactory } from 'tsyringe';
import {
  OWWindow,
  OWWindowScreenAnchor,
  OWWindowAnchorTypes,
} from '../../odk-ts';
import { TimerService } from 'app-core/src/services/timer.service';
import { AsyncLock } from 'app-core/src/utils/async-lock';
import {
  kNotificationWindowName,
  KTesterWindowName,
  kDITokenOwTesterWindow,
  kNameOfDownloadService,
  kNameOfSimplePluginService,
  kNameOfRegistryWrapper,
} from 'app-core/src/constants/constants';
import {
  kNameOfNotificationWindowManagerService,
  INotificationWindowManagerService,
} from 'app-core/src/interfaces/notification-window-manager-service.interface';
import {
  ITesterWindowManagerService,
  kNameOfTesterWindowManagerService,
} from '../interfaces/tester-window-manager-service.interface';
import { NotificationWindowService } from 'app-core/src/services/notification-window.service';
import {
  kNameOfRemoteObjectHttpFile,
  RemoteObjectHttpFile,
} from 'app-core/src/services/remote-object/remote-object-http-file';
import {
  kDITRemoteObjectRetrieverServiceOptions,
  RemoteObjectRetrieverService,
} from 'app-core/src/services/remote-object/remote-object-retriever.service';
import { TesterWindowService } from '../services/tester-window.service';
import configurations from '../../configurations.json';
import { IRemoteObject } from 'app-core/src/services/remote-object/remote-object.interface';
import { OverwolfDownloadService } from '../services/download-service/download.service';
import { IDownloadService } from 'app-core/src/interfaces/download-service.interface';
import { kDITRecommendationRemoteObjectRetrieverService } from 'app-core/src/services/datastore-service/remote-configurations.service';
import { RecommendationConfig } from 'app-core/src/services/datastore-service/models/recommendation-config.model';
import {
  kDITOWGamesWrapper,
  kDITOWTrackRSWrapper,
  kDITOWUtilsWrapper,
  kDITOWSettingsWrapper,
  kDITOWGameListenerWrapper,
  kDITOWExtensionWrapper,
  kDITOWWrapper,
  kDITOWCustomURLWrapper,
  kDIOWIOPathsWrapper,
  kDIConfigurations,
  kDIOWOWSettingsGamesWrapper,
  kDITokenOwNotificationWindow,
  kDITokenWindowListener,
  kDITokenNotificationWindowScreenAnchor,
  kDITGameEventsListener,
  kDIRobloxListener,
} from 'app-core/src/constants/injectionTokens';
import { DelegateProvider } from 'app-core/src/interfaces/delegate-provider.interface';
import { IOwGamesWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-games-wrapper.interface';
import { IOWSettingsWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-settings-wrapper.interface';
import { IOwTrackRSEventWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-track-rs-event.interface';
import { IOWUtilsWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-utils-wrapper.interface';
import { OWTrackRSWrapper } from '../../shared/wrappers/ow-track-rs-event';
import { OWGameListenerWrapper } from '../ow-odk-wrappers/ow-game-listener-wrapper';
import { OWGamesWrapper } from '../ow-odk-wrappers/ow-games-wrapper';
import { OWUtilsWrapper } from '../ow-odk-wrappers/ow-utils-wrapper';
import { OWSettingsWrapper } from '../ow-odk-wrappers/ow-settings-wrapper';
import { IOWExtensionWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-extension-wrapper.interface';
import { OWExtensionWrapper } from '../ow-odk-wrappers/ow-extension-wrapper';
import { IOWWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-wrapper.interface';
import { OWWrapper } from '../../shared/wrappers/ow-wrapper';
import { IOWCustomURLWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-custom-url-wrapper.interface';
import { OWCustomURLWrapper } from '../../shared/wrappers/ow-custom-url-wrapper';
import {
  kNameOfNotificationAnalyticsService,
  INotificationAnalyticsService,
  NotificationAnalyticsService,
} from 'app-core/src/services/notification-analytics/notification-analytics.service';
import { IOWIOPathsWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-io-paths-wrapper.interface';
import { OWIOPathsWrapper } from '../ow-odk-wrappers/ow-io-paths-wrapper';
import {
  IConfigurations,
  RemoteObjectRetrieverServiceOptions,
} from 'app-core/src/interfaces/configurations.interface';
import { IOWGameListenerWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-game-listener-wrapper.interface';
import { IOWSettingsGamesWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-settings-games-wrapper.interface';
import { OWSettingsGamesWrapper } from '../ow-odk-wrappers/ow-settings-games-wrapper';
import { IOWWindowWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-window-wrapper.interface';
import { IOWWindowScreenAnchorWrapper } from 'app-core/src/interfaces/ow-wrappers/ow-window-screen-anchor-wrapper.interface';
import { IOWWindowsListenerDelegateProvider } from 'app-core/src/interfaces/ow-wrappers/ow-window-listener-delegate-wrapper.interface';
import { OWWindowListenerWrapper } from '../../shared/wrappers/ow-window-listener-wrapper';
import { OwSimplePluginService } from '../services/simple-plugin-service.ts/simple-plugin.service';
import { OwRegistryWrapper } from '../services/registry-wrapper';
import { IOWGameEventsListener } from 'app-core/src/interfaces/ow-wrappers/ow-game-events-listener-wrapper.interface';
import { OwGameEventsWrapper } from '../ow-odk-wrappers/ow-game-events-listener-wrapper';
import { IRobloxListener } from 'app-core/src/interfaces/ow-wrappers/roblox-listener.interface';
import { RobloxListenerService } from '../services/roblox-listener.service';

// -----------------------------------------------------------------------------
// Here we only register services that reside in the background page.
// For services that are exposed to other windows of the app, see
// |ExternalServicesRegistry|
@registry([
  // ---------------------------------------------------------------------------
  // Overwolf notification window object
  {
    token: kDITokenOwNotificationWindow,
    useFactory: instancePerContainerCachingFactory<IOWWindowWrapper>(() => {
      return new OWWindow(kNotificationWindowName);
    }),
  },
  // ---------------------------------------------------------------------------
  // Overwolf Screen anchor service to wrap notification window
  {
    token: kDITokenNotificationWindowScreenAnchor,
    useFactory:
      instancePerContainerCachingFactory<IOWWindowScreenAnchorWrapper>((c) => {
        return new OWWindowScreenAnchor({
          window: c.resolve(kDITokenOwNotificationWindow),
          anchorType: OWWindowAnchorTypes.bottomRight,
          isTopMost: true,
        });
      }),
  },
  // ---------------------------------------------------------------------------
  // nop
  {
    token: kNameOfNotificationWindowManagerService,
    useFactory:
      instancePerContainerCachingFactory<INotificationWindowManagerService>(
        (c) => {
          return c.resolve(NotificationWindowService);
        },
      ),
  },
  // ---------------------------------------------------------------------------
  // Overwolf tester window object
  {
    token: kDITokenOwTesterWindow,
    useFactory: instancePerContainerCachingFactory<OWWindow>(() => {
      return new OWWindow(KTesterWindowName);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kNameOfTesterWindowManagerService,
    useFactory: instancePerContainerCachingFactory<ITesterWindowManagerService>(
      (c) => {
        return c.resolve(TesterWindowService);
      },
    ),
  },
  // ---------------------------------------------------------------------------
  {
    token: kNameOfNotificationAnalyticsService,
    useFactory:
      instancePerContainerCachingFactory<INotificationAnalyticsService>((c) => {
        return c.resolve(NotificationAnalyticsService);
      }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kNameOfRemoteObjectHttpFile,
    useFactory: instancePerContainerCachingFactory<IRemoteObject>((c) => {
      return c.resolve(RemoteObjectHttpFile);
    }),
  },
  // ---------------------------------------------------------------------------
  // RemoteObjectRetrieverServiceOptions
  {
    token: kDITRemoteObjectRetrieverServiceOptions,
    useFactory:
      instancePerContainerCachingFactory<RemoteObjectRetrieverServiceOptions>(
        () => {
          const {
            configUrl,
            maxRetries,
            retrievalIntervalInMs,
            waitIntervalBetweenRetriesInMs,
          } = configurations.remoteObjectRetrieverService;

          const options: RemoteObjectRetrieverServiceOptions = {
            configUrl,
            retrievalIntervalInMs,
            maxRetries,
            waitIntervalBetweenRetriesInMs,
          };

          return options;
        },
      ),
  },
  // ---------------------------------------------------------------------------
  // Timer for RemoteObjectRetreiverService

  // ---------------------------------------------------------------------------
  // RemoteObjectRetrieverService initialization
  {
    token: kDITRecommendationRemoteObjectRetrieverService,
    useFactory: instancePerContainerCachingFactory<
      RemoteObjectRetrieverService<RecommendationConfig>
    >((c) => {
      return new RemoteObjectRetrieverService<RecommendationConfig>(
        c.resolve(kDITRemoteObjectRetrieverServiceOptions),
        c.resolve(kNameOfRemoteObjectHttpFile),
        c.resolve(TimerService),
        c.resolve(AsyncLock),
      );
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kNameOfDownloadService,
    useFactory: instancePerContainerCachingFactory<IDownloadService>((c) => {
      return c.resolve(OverwolfDownloadService);
    }),
  },

  // ---------------------------------------------------------------------------
  {
    token: kNameOfSimplePluginService,
    useFactory: instancePerContainerCachingFactory<OwSimplePluginService>(
      (c) => {
        return c.resolve(OwSimplePluginService);
      },
    ),
  },

  // ---------------------------------------------------------------------------
  {
    token: kNameOfRegistryWrapper,
    useFactory: instancePerContainerCachingFactory<OwRegistryWrapper>((c) => {
      return c.resolve(OwRegistryWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDIConfigurations,
    useFactory: instancePerContainerCachingFactory<IConfigurations>(() => {
      return configurations as IConfigurations;
    }),
  },

  // ---------------------------------------------------------------------------
  // OW Wrappers
  // ---------------------------------------------------------------------------
  {
    token: kDITOWGamesWrapper,
    useFactory: instancePerContainerCachingFactory<IOwGamesWrapper>((c) => {
      return c.resolve(OWGamesWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITOWTrackRSWrapper,
    useFactory: instancePerContainerCachingFactory<IOwTrackRSEventWrapper>(
      (c) => {
        return c.resolve(OWTrackRSWrapper);
      },
    ),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITOWUtilsWrapper,
    useFactory: instancePerContainerCachingFactory<IOWUtilsWrapper>((c) => {
      return c.resolve(OWUtilsWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITOWSettingsWrapper,
    useFactory: instancePerContainerCachingFactory<IOWSettingsWrapper>((c) => {
      return c.resolve(OWSettingsWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITOWGameListenerWrapper,
    useFactory: instancePerContainerCachingFactory<
      DelegateProvider<IOWGameListenerWrapper>
    >((c) => {
      return c.resolve(OWGameListenerWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITOWExtensionWrapper,
    useFactory: instancePerContainerCachingFactory<IOWExtensionWrapper>((c) => {
      return c.resolve(OWExtensionWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITOWWrapper,
    useFactory: instancePerContainerCachingFactory<IOWWrapper>((c) => {
      return c.resolve(OWWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITOWCustomURLWrapper,
    useFactory: instancePerContainerCachingFactory<IOWCustomURLWrapper>((c) => {
      return c.resolve(OWCustomURLWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDIOWIOPathsWrapper,
    useFactory: instancePerContainerCachingFactory<IOWIOPathsWrapper>((c) => {
      return c.resolve(OWIOPathsWrapper);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDIOWOWSettingsGamesWrapper,
    useFactory: instancePerContainerCachingFactory<IOWSettingsGamesWrapper>(
      (c) => {
        return c.resolve(OWSettingsGamesWrapper);
      },
    ),
  },

  // ---------------------------------------------------------------------------
  {
    token: kDITGameEventsListener,
    useFactory: instancePerContainerCachingFactory<IOWGameEventsListener>(
      (c) => {
        return c.resolve(OwGameEventsWrapper);
      },
    ),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDIRobloxListener,
    useFactory: instancePerContainerCachingFactory<IRobloxListener>((c) => {
      return c.resolve(RobloxListenerService);
    }),
  },
  // ---------------------------------------------------------------------------
  {
    token: kDITokenWindowListener,
    useFactory:
      instancePerContainerCachingFactory<IOWWindowsListenerDelegateProvider>(
        (c) => {
          return c.resolve(
            OWWindowListenerWrapper,
          ) as IOWWindowsListenerDelegateProvider;
        },
      ),
  },
])
export class BackgroundRegistry {}
